import router from "@/router/index.js";

// Vercon Backend API Service for Battery Configurator (BattCon)
class BattConService {
  constructor() {
    this.baseUrl = process.env.VUE_APP_BACKEND_URL || "https://localhost:7052";
  }

  // Generic API request handler with error handling
  async makeRequest(url, options = {}) {
    try {
      if (options.headers)
        options.headers["content-type"] = "application/json;charset=UTF-8";
      else
        options.headers = { "content-type": "application/json;charset=UTF-8" };
      const response = await fetch(`${this.baseUrl}${url}`, {
        ...options,
      });

      if (response.ok) {
        if (
          response.headers.get("content-length") &&
          response.headers.get("content-length") != "0"
        )
          return await response.json();
        else return await Promise.resolve();
      }
      const errorData = await response.text();
      throw new Error(
        `HTTP ${response.status}: ${errorData || response.statusText}`,
      );
      // or via promises?
      // return Promise.reject(await response.text());
    } catch (error) {
      console.error("API Request failed:", error);
      throw error;
      // or via promises?
      // return Promise.reject(error);
    }
  }

  // =====================================
  // Battery Configurator Endpoints
  // =====================================

  /**
   * Get battery configurator configuration
   * @param {string} installerKey - Installer's configuration key
   * @returns {Promise<BattConConfig>}
   */
  async getBattConConfig(installerKey, test = false) {
    const url =
      `/BattCon/GetBattConConfig?installer_key=${encodeURIComponent(
        installerKey,
      )}` + (test === true ? "&themeTest=true" : "");

    try {
      return await this.makeRequest(url);
    } catch (error) {
      // Fallback: if WHES fails, try alius
      if (installerKey === "whes" && error.message.includes("404")) {
        console.warn(`WHES installer key not found, falling back to alius`);
        const fallbackUrl =
          `/BattCon/GetBattConConfig?installer_key=alius` +
          (test === true ? "&themeTest=true" : "");
        return await this.makeRequest(fallbackUrl);
      }
      throw error;
    }
  }

  /**
   * Get battery configurator configuration
   * @param {string} installerKey - Installer's configuration key
   * @returns {Promise<BattConConfigBattery[]>}
   */
  async GetBattConBatteries(installerKey) {
    const url = `/BattCon/GetBattConBatteries?installer_key=${encodeURIComponent(
      installerKey,
    )}`;

    try {
      return await this.makeRequest(url);
    } catch (error) {
      // Fallback: if WHES fails, try alius
      if (installerKey === "whes" && error.message.includes("404")) {
        console.warn(
          `WHES installer key not found in GetBattConBatteries, falling back to alius`,
        );
        const fallbackUrl = `/BattCon/GetBattConBatteries?installer_key=alius`;
        return await this.makeRequest(fallbackUrl);
      }
      throw error;
    }
  }

  /**
   * Calculate battery performance and costs
   * @param {string} installerKey - Installer's configuration key
   * @param {number} batteryId - Selected battery ID
   * @param {BattConParameters} parameters - Battery calculation parameters
   * @returns {Promise<BattConResult>}
   */
  async getBattery(installerKey, batteryId, parameters) {
    const url = `/BattCon/GetBattery?installer_key=${encodeURIComponent(
      installerKey,
    )}&batteryId=${batteryId}`;

    try {
      return await this.makeRequest(url, {
        method: "POST",
        body: JSON.stringify(parameters),
      });
    } catch (error) {
      // Fallback: if WHES fails, try alius
      if (installerKey === "whes" && error.message.includes("404")) {
        console.warn(
          `WHES installer key not found in getBattery, falling back to alius`,
        );
        const fallbackUrl = `/BattCon/GetBattery?installer_key=alius&batteryId=${batteryId}`;
        return await this.makeRequest(fallbackUrl, {
          method: "POST",
          body: JSON.stringify(parameters),
        });
      }
      throw error;
    }
  }

  /**
   * Calculate battery performance and costs
   * @param {string} installerKey - Installer's configuration key
   * @param {number} batteryId - Selected battery ID
   * @param {BattConParameters} parameters - Battery calculation parameters
   * @returns {Promise<BattConBattery>}
   */
  async getBatteries(installerKey, parameters) {
    const url = `/BattCon/GetBatteries?installer_key=${encodeURIComponent(
      installerKey,
    )}`;

    try {
      return await this.makeRequest(url, {
        method: "POST",
        body: JSON.stringify(parameters),
      });
    } catch (error) {
      // Fallback: if WHES fails, try alius
      if (installerKey === "whes" && error.message.includes("404")) {
        console.warn(
          `WHES installer key not found in getBatteries, falling back to alius`,
        );
        const fallbackUrl = `/BattCon/GetBatteries?installer_key=alius`;
        return await this.makeRequest(fallbackUrl, {
          method: "POST",
          body: JSON.stringify(parameters),
        });
      }
      throw error;
    }
  }

  getMediaUrl(mediaName) {
    let installerKey = router.currentRoute.value.params.installer_key;
    if (installerKey == undefined) installerKey = "alius";
    return `${this.baseUrl}/BattCon/GetMedia?installer_key=${encodeURIComponent(
      installerKey,
    )}&usage=${encodeURIComponent(mediaName)}`;
  }

  /**
   * Create a lead from battery configurator
   * @param {string} installerKey - Installer's configuration key
   * @param {LeadData} leadData - Lead information
   * @returns {Promise<any>}
   */
  async createLead(installerKey, capchaCode, leadData) {
    const url = `/BattCon/CreateLead?installer_key=${encodeURIComponent(
      installerKey,
    )}&capchaCode=${encodeURIComponent(capchaCode)}`;

    try {
      return await this.makeRequest(url, {
        method: "POST",
        body: JSON.stringify(leadData),
      });
    } catch (error) {
      // Fallback: if WHES fails, try alius
      if (installerKey === "whes" && error.message.includes("404")) {
        console.warn(
          `WHES installer key not found in createLead, falling back to alius`,
        );
        const fallbackUrl = `/BattCon/CreateLead?installer_key=alius`;
        return await this.makeRequest(fallbackUrl, {
          method: "POST",
          body: JSON.stringify(leadData),
        });
      }
      throw error;
    }
  }

  // =====================================
  // Helper Methods
  // =====================================

  /**
   * Validate BattConParameters before sending
   * @param {BattConParameters} parameters
   * @returns {boolean}
   */
  validateBattConParameters(parameters) {
    const required = [
      "adults",
      "children",
      "has_ev",
      "has_heatpump",
      "power_usage",
      "solarPanelAmount",
      "latitude",
      "longitude",
    ];

    for (const field of required) {
      if (parameters[field] === undefined || parameters[field] === null) {
        throw new Error(`Missing required field: ${field}`);
      }
    }

    if (!Array.isArray(parameters.solarPanels)) {
      throw new Error("solarPanels must be an array");
    }

    // Validate solar panels
    parameters.solarPanels.forEach((panel, index) => {
      const panelRequired = [
        "azimuth",
        "angle",
        "flatRoofOrientation",
        "amount",
      ];
      for (const field of panelRequired) {
        if (panel[field] === undefined || panel[field] === null) {
          throw new Error(
            `Missing required field in solar panel ${index}: ${field}`,
          );
        }
      }
    });

    return true;
  }

  async verifyCapchaCode(capchaCode) {
    try {
      // Call the actual backend API
      const url = `${
        this.baseUrl
      }/BattCon/VerifyCapchaCode?capchaCode=${encodeURIComponent(capchaCode)}`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "text/plain",
        },
      });

      return response;
      //    if (!response.ok) {
      //      throw new Error(`HTTP error! status: ${response.status}`);
      //    }

      //    const data = await response.json();

      //    return data;
    } catch (error) {
      console.error("Verifying capchaCode failed:", error);

      return error;
    }
  }

  /**
   * Create a properly formatted BattConParameters object
   * @param {Object} config - Configuration object
   * @returns {BattConParameters}
   */
  createBattConParameters({
    occupants = 2,
    hasEv = "no",
    hasHeatpump = "no",
    powerUsage = 3500,
    solarPanelAmount = 12,
    solarPanels = [],
    latitude = 52.3676,
    longitude = 4.9041,
    selectedBattery = null,
  }) {
    return {
      occupants,
      has_ev: hasEv,
      has_heatpump: hasHeatpump,
      power_usage: powerUsage,
      solarPanelAmount,
      solarPanels,
      latitude,
      longitude,
      selectedBattery,
    };
  }

  /**
   * Create lead data object
   * @param {Object} config - Lead configuration
   * @returns {LeadData}
   */
  createLeadData({ name, email, phoneNumber, remark = "", configuration }) {
    if (!name || !email || !phoneNumber || !configuration) {
      throw new Error(
        "Missing required lead data: name, email, phoneNumber, and configuration are required",
      );
    }

    return {
      name,
      email,
      phoneNumber,
      remark,
      configuration,
    };
  }

  // =====================================
  // Mock/Development Methods
  // =====================================

  // /**
  //  * Generate mock battery config for development
  //  * @returns {BattConConfig}
  //  */
  // getMockBattConConfig() {
  //   return {
  //     primaryThemeColor: 0x6B46C1, // Purple
  //     secondaryThemeColor: 0xF59E0B, // Amber
  //     batteries: [
  //       {
  //         id: 1,
  //         name: "Tesla Powerwall 2",
  //         capacity: 13.5,
  //         priceFrom: 8500
  //       },
  //       {
  //         id: 2,
  //         name: "LG Chem RESU 10H",
  //         capacity: 9.8,
  //         priceFrom: 6500
  //       },
  //       {
  //         id: 3,
  //         name: "Sonnen eco 8",
  //         capacity: 8.0,
  //         priceFrom: 7200
  //       }
  //     ]
  //   };
  // }

  /**
   * Generate mock battery result for development
   * @returns {BattConResult}
   */
  getMockBattConResult() {
    return {
      originalSelfConsumption: 35.2,
      originalElectricityReturn: 64.8,
      withBatterySelfConsumption: 68.5,
      withBatteryElectricityReturn: 31.5,
      electric: [420, 380, 340, 310, 280, 260, 270, 290, 320, 360, 390, 410],
      solar: [120, 180, 280, 420, 550, 620, 580, 520, 380, 250, 150, 100],
      selfConsumption: [85, 82, 78, 75, 72, 70, 71, 73, 76, 79, 81, 84],
    };
  }
}

export default new BattConService();
